import { TreeNode } from "@/types/tree";
import { documentStore } from "@/store/store";
import { findNode, getNodeParent } from "../tree/tree";
import { isObject } from "@/util/objects";
import { COLUMN_SLUG, COLUMNS_SLUG } from "@/lib/elements/constants";
import { rewidthColumnsAndCommitToStore } from "@/lib/columns/rewidthing";
import NotificationLogger from "@/lib/notifications/notifications";
import { parseFloatAndFixTo2Digits } from "@/util/math";

export const PATH_TO_WIDTH_PROPERTY = `design.size.width`;
export const PATH_TO_SIZE_SECTION = `design.size`;

export function isColumnElement(element: TreeNode | false | undefined) {
  if (!element) return false;
  return element.data.type === COLUMN_SLUG;
}

export function isColumnsElement(element: TreeNode | false) {
  if (!element) return false;
  return element.data.type === COLUMNS_SLUG;
}

export function getColumnWidth(column: TreeNode) {
  const columnWidth = parseFloat(
    String(
      documentStore.getPropertyValue(
        column.id,
        `${PATH_TO_WIDTH_PROPERTY}.number`
      )
    )
  );

  if (Number.isNaN(columnWidth)) return 0;
  return columnWidth;
}

export function getColumnsElement(
  columnOrColumnsElementOrId: TreeNode | number
) {
  if (typeof columnOrColumnsElementOrId === "number") {
    const maybeColumnsOrColumn = findNode(
      documentStore.document.tree,
      columnOrColumnsElementOrId
    );

    if (maybeColumnsOrColumn && isColumnsElement(maybeColumnsOrColumn)) {
      return maybeColumnsOrColumn;
    } else if (isColumnElement(maybeColumnsOrColumn)) {
      const parent = getNodeParent(
        documentStore.document.tree,
        columnOrColumnsElementOrId
      );

      if (parent && isColumnsElement(parent)) {
        return parent;
      }
    }
  } else {
    if (
      columnOrColumnsElementOrId &&
      isColumnsElement(columnOrColumnsElementOrId)
    ) {
      return columnOrColumnsElementOrId;
    } else if (
      columnOrColumnsElementOrId &&
      isColumnElement(columnOrColumnsElementOrId)
    ) {
      const parent = getNodeParent(
        documentStore.document.tree,
        columnOrColumnsElementOrId.id
      );
      if (parent && isColumnsElement(parent)) {
        return parent;
      }
    }
  }

  return false;
}

export function doColumnWidthsAddUpTo100(
  columnsElement: TreeNode
): boolean | -1 {
  if (!isColumnsElement(columnsElement)) return -1;

  const width = sumAllColumnWidths(columnsElement.children);

  return width === 100;
}

export function sumAllColumnWidths(columnArray: TreeNode[]): number | false {
  if (columnArray.every(isColumnElement)) {
    const width = columnArray.reduce((accumulatedWidth, column) => {
      return accumulatedWidth + getColumnWidth(column);
    }, 0);

    if (Number.isNaN(width)) return false;

    return parseFloatAndFixTo2Digits(width);
  }
  return false;
}

export function getDifferenceInWidths(
  previousWidthPropertyValue: unknown,
  newWidthPropertyValue: unknown
) {
  if (
    isObject(previousWidthPropertyValue) &&
    "number" in previousWidthPropertyValue &&
    isObject(newWidthPropertyValue) &&
    "number" in newWidthPropertyValue
  ) {
    const widthDiff =
      // @ts-ignore
      parseFloat(String(previousWidthPropertyValue.number)) -
      // @ts-ignore
      parseFloat(String(newWidthPropertyValue.number));

    if (!Number.isNaN(widthDiff)) {
      return parseFloatAndFixTo2Digits(widthDiff);
    }
  }

  return -10000;
}

// fail-safe in case the user somehow messed up the sizes. Shouldn't happen!
export function rewidthIfColumnsNotAddUpTo100(columnsElement: TreeNode) {
  if (doColumnWidthsAddUpTo100(columnsElement) === false) {
    NotificationLogger.log.message(
      `There was an error while resizing the columns in element "${columnsElement.id}". They've all been resized to the default value.`
    );

    rewidthColumnsAndCommitToStore(columnsElement);
  }
}
